<template>
  <div class="base-tooltip">
    <div
      ref="element"
      v-on="hoverHandlers"
    >
      <!--
      @slot Default Slot - Required
      -->
      <slot :hover="isHover" />
    </div>
    <div
      ref="tooltip"
      class="tooltip"
      :style="{ minWidth }"
    >
      <div :style="{ 'max-width': maxWidth }">
        <!--
        @slot Tooltip Slot - Content shown inside the tooltip
        -->
        <slot name="tooltip">
          {{ text }}
        </slot>
      </div>
      <div class="tooltip__arrow" />
    </div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import Hoverable from '../mixins/hoverable';

export default {
  name: 'BaseTooltip',
  mixins: [Hoverable],
  props: {
    text: {
      type: String,
      default: 'Tooltip text',
    },
    maxWidth: {
      type: String,
      default: '320px',
    },
    minWidth: {
      type: String,
      default: '0',
    },
    offset: {
      default: true,
    },
  },
  data() {
    return {
      instance: null,
      element: '',
      tooltip: '',
    };
  },
  watch: {
    isHover(val) {
      if (val) {
        this.create();
        this.tooltip.setAttribute('data-show', '');
      } else {
        this.tooltip.removeAttribute('data-show');
      }
    },
    text() {
      this.destroy();
      this.create();
    },
  },
  mounted() {
    this.element = this.$refs.element;
    this.tooltip = this.$refs.tooltip;

    // Move tooltip outside component
    document.body.appendChild(this.tooltip);

    // Events
    this.tooltip.addEventListener('transitionend', () => {
      if (!this.isHover) {
        this.$emit('closed');
        this.$nextTick(() => {
          this.destroy();
        });
      } else {
        this.$emit('opened');
      }
    });
  },
  beforeDestroy() {
    document.body.removeChild(this.tooltip);
  },
  methods: {
    create() {
      if (this.instance === null) {
        const modifiers = [];
        if (this.offset) {
          modifiers.push({
            name: 'offset',
            options: {
              offset: this.offset === true ? [0, 11] : this.offset,
            },
          });
        }
        this.instance = createPopper(this.element, this.tooltip, {
          modifiers,
          placement: 'top',
          strategy: 'fixed',
        });
        // this.instance.update();
      }
    },
    destroy() {
      if (this.instance !== null) {
        this.instance.destroy();
        this.instance = null;
      }
    },
    close() {
      this.isHover = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.base-tooltip {
  display: inline-block;
}
.tooltip {
  background-color: #333;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 13px;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
  transform: translateY(-99999px);
  z-index: $zindex-tooltip;

  &[data-show] {
    opacity: 1 !important;
    transform: translateY(0);
  }
}

.tooltip__arrow,
.tooltip__arrow::before {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  z-index: -1;
}

.tooltip__arrow::before {
  content: "";
  transform: rotate(45deg);
  background: #333;
}

.tooltip[data-popper-placement^="top"] > .tooltip__arrow {
  bottom: -4px;
}

.tooltip[data-popper-placement^="bottom"] > .tooltip__arrow {
  top: -4px;
}

.tooltip[data-popper-placement^="left"] > .tooltip__arrow {
  right: -4px;
}

.tooltip[data-popper-placement^="right"] > .tooltip__arrow {
  left: -4px;
}
</style>
